import React, { useState, useContext, FunctionComponent } from "react";
import { Button } from "@cmsgov/ds-medicare-gov";
import { ExtraSSHelp, HowDoYouGetHelp } from ".";
import {
  AppContextType,
  ActionType,
  LowIncomeSubsidyOption,
  LowIncomeSubsidyAmount,
  UserLanguage,
  LowIncomeSubsidyStatus,
} from "../../../@types";
import { RouteComponentProps, withRouter } from "react-router";
import { AppContext } from "../../../app/store";
import {
  useTranslate,
  sendTealiumEvent,
  initiateLogin,
} from "../../../helpers";
import {
  getNewLISCodeFromUserOptions,
  fetchCurrentPlan,
} from "../../../helpers/planHelpers";

interface LISQuestionsProps extends RouteComponentProps {
  nextPage: () => void;
}

const LISQuestions: FunctionComponent<LISQuestionsProps> = ({ nextPage }) => {
  const t = useTranslate();
  const [helpType, setHelpType] = useState<undefined | LowIncomeSubsidyOption>(
    undefined
  );
  const [
    amountOfSSHelp,
    setAmountOfSSHelp,
  ] = useState<LowIncomeSubsidyAmount | null>(null);
  const { state, dispatch } = useContext<AppContextType>(AppContext);

  // Determines if the user has selected a Help Type that does not require further input
  const undefinitiveLowIncomeSubsidyOption = [
    LowIncomeSubsidyOption.SS,
    LowIncomeSubsidyOption.Unsure,
  ];
  const helpTypeIsDefinitive =
    helpType && !undefinitiveLowIncomeSubsidyOption.includes(helpType);

  // Calculate if we have what we need to allow the user to continue
  const canContinue =
    (helpType && helpTypeIsDefinitive) || amountOfSSHelp !== null;

  const handleContinuing = (): void => {
    const selectedLIS = getNewLISCodeFromUserOptions(helpType, amountOfSSHelp);
    if (selectedLIS !== state.lis) {
      dispatch({
        type: ActionType.UPDATE_LIS,
        payload: getNewLISCodeFromUserOptions(helpType, amountOfSSHelp),
      });
      fetchCurrentPlan(
        state.beneficiary,
        state.pharmacies,
        selectedLIS || LowIncomeSubsidyStatus.LIS_NO_HELP,
        dispatch,
        state.nextYearCoverage,
        state.futureLis
      );
    }

    nextPage();
  };

  const handleLoginClick = (): void => {
    /* istanbul ignore next: tested elsewhere */
    sendTealiumEvent("Find Plans - Choose Plans", "Login");
    initiateLogin(state.language === UserLanguage.SPANISH);
  };

  return (
    <>
      <HowDoYouGetHelp
        answeredTypeOfHelp={(value: LowIncomeSubsidyOption): void =>
          setHelpType(value)
        }
      />

      {helpType && helpType === LowIncomeSubsidyOption.SS && (
        <ExtraSSHelp
          updateAmountOfSSHelp={(value: LowIncomeSubsidyAmount | null): void =>
            setAmountOfSSHelp(value)
          }
        />
      )}

      {helpType && helpType === LowIncomeSubsidyOption.Unsure && (
        <section data-testid="continue" className="ContinueSection">
          <Button
            onClick={handleLoginClick}
            className="ds-u-margin-right--1"
            variation="primary"
          >
            Log in
          </Button>
          <Button
            className="ds-c-button--primary-alt-outline"
            onClick={(): void => {
              sendTealiumEvent(
                "Find Plans - Choose Plans",
                "Continue without Logging in"
              );
              handleContinuing();
            }}
          >
            {t("landing_page.continue_without_login")}
          </Button>
        </section>
      )}

      {canContinue && (
        <section data-testid="continue" className="ContinueSection">
          <Button
            variation="primary"
            onClick={(): void => {
              sendTealiumEvent("Find Plans", "Continue");
              handleContinuing();
            }}
          >
            {t("landing_page.continue")}
          </Button>
        </section>
      )}
    </>
  );
};

export default withRouter(LISQuestions);
